import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CalendarIcon,
  Clock,
  Package,
  Plus,
  X,
  Info,
  Sparkles,
  ImageIcon,
  Store,
  LayoutGrid,
  Bell,
  Heart,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import { format } from 'date-fns';

import { useQueries } from '@tanstack/react-query';
import { useCampaign } from '../hooks/use-campaign';
import { useCreateCampaign } from '../hooks/use-create-campaign';
import { useUpdateCampaign } from '../hooks/use-update-campaign';
import { useAdTypeConfigs, AD_TYPE_DEFAULTS } from '../hooks/use-ad-type-config';
import { ProductPickerDialog, type SelectedProduct } from '../components/product-picker-dialog';
import type { AdType } from '../types/ads.types';
import { campaignSchema, type CampaignFormData } from '@/lib/validations/ads.schema';
import { productsService } from '@/features/products/services/products.service';
import { getImageUrl } from '@/lib/utils';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/molecules/popover';
import { Calendar } from '@/components/atoms/calendar';
import { cn, formatPrice } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import {
  CURRENCIES,
  CURRENCY_SYMBOLS,
  EUR_TO_USD,
  EUR_TO_KMF,
  type Currency,
} from '@/lib/utils/constants';

// Convert amount from one currency to EUR (base currency)
function toEur(amount: number, fromCurrency: Currency): number {
  if (fromCurrency === 'EUR') return amount;
  if (fromCurrency === 'USD') return amount / EUR_TO_USD;
  if (fromCurrency === 'KMF') return amount / EUR_TO_KMF;
  return amount;
}

// Convert amount from EUR to target currency
function fromEur(amountEur: number, toCurrency: Currency): number {
  if (toCurrency === 'EUR') return amountEur;
  if (toCurrency === 'USD') return amountEur * EUR_TO_USD;
  if (toCurrency === 'KMF') return amountEur * EUR_TO_KMF;
  return amountEur;
}

interface CampaignFormContainerProps {
  campaignId?: string;
}

const CAMPAIGN_TYPES: { value: AdType; label: string; icon: LucideIcon; descriptionKey: string; descriptionFallback: string }[] = [
  {
    value: 'sponsored_product',
    label: 'ads.types.sponsored_product',
    icon: Sparkles,
    descriptionKey: 'ads.types.sponsored_product_desc',
    descriptionFallback: 'Mettez votre produit en avant dans les résultats',
  },
  {
    value: 'homepage_carousel',
    label: 'ads.types.homepage_carousel',
    icon: ImageIcon,
    descriptionKey: 'ads.types.homepage_carousel_desc',
    descriptionFallback: 'Affichez votre produit sur la page d\'accueil',
  },
  {
    value: 'featured_shop',
    label: 'ads.types.featured_shop',
    icon: Store,
    descriptionKey: 'ads.types.featured_shop_desc',
    descriptionFallback: 'Mettez votre boutique en avant',
  },
  {
    value: 'category_banner',
    label: 'ads.types.category_banner',
    icon: LayoutGrid,
    descriptionKey: 'ads.types.category_banner_desc',
    descriptionFallback: 'Bannière dans une catégorie',
  },
  // DISABLED: push_all_users - Too dangerous, can lead to spam and app uninstalls
  // Best practices recommend segmented targeting (see: Pushwoosh, MoEngage, CleverTap)
  // Broadcast to all users has only 21% retention vs 39% for targeted notifications
  // {
  //   value: 'push_all_users',
  //   label: 'ads.types.push_all_users',
  //   icon: Users,
  //   descriptionKey: 'ads.types.push_all_users_desc',
  //   descriptionFallback: 'Envoyez une notification à tous',
  // },
  {
    value: 'push_shop_subscribers',
    label: 'ads.types.push_shop_subscribers',
    icon: UserCheck,
    descriptionKey: 'ads.types.push_shop_subscribers_desc',
    descriptionFallback: 'Notifiez vos abonnés',
  },
  {
    value: 'push_product_likers',
    label: 'ads.types.push_product_likers',
    icon: Heart,
    descriptionKey: 'ads.types.push_product_likers_desc',
    descriptionFallback: 'Notifiez ceux qui aiment vos produits',
  },
  {
    value: 'push_single_product_likers',
    label: 'ads.types.push_single_product_likers',
    icon: Bell,
    descriptionKey: 'ads.types.push_single_product_likers_desc',
    descriptionFallback: 'Notifiez les fans d\'un produit',
  },
];

// Campaign types that require product selection
const PRODUCT_CAMPAIGN_TYPES: AdType[] = [
  'sponsored_product',
  'homepage_carousel',
  'push_product_likers',
  'push_single_product_likers',
  'category_banner', // Banner is generated from selected products
];

export function CampaignFormContainer({ campaignId }: CampaignFormContainerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditing = !!campaignId;
  const { currency: defaultCurrency } = useConfigStore();

  const [isProductPickerOpen, setIsProductPickerOpen] = React.useState(false);
  const [selectedProducts, setSelectedProducts] = React.useState<SelectedProduct[]>([]);
  const [bannerText, setBannerText] = React.useState('');

  // Simplified budget state - user enters in their preferred currency
  const [selectedCurrency, setSelectedCurrency] = React.useState<Currency>(defaultCurrency);
  const [budget, setBudget] = React.useState<number>(10);
  const [dailyBudget, setDailyBudget] = React.useState<number | undefined>(undefined);
  const [bidAmount, setBidAmount] = React.useState<number | undefined>(undefined);

  const { data: campaign, isLoading: isLoadingCampaign } = useCampaign(campaignId);
  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign();
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign();
  const { data: adTypeConfigs } = useAdTypeConfigs();

  // Fetch product details for campaign items to get images
  const productIds = React.useMemo(() => {
    if (!campaign?.items) return [];
    return campaign.items
      .filter((item) => item.productRefId)
      .map((item) => item.productRefId!);
  }, [campaign?.items]);

  const productQueries = useQueries({
    queries: productIds.map((id) => ({
      queryKey: ['product', id],
      queryFn: () => productsService.getProduct(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    })),
  });

  // Create a map of productRefId to image URL
  const productImagesMap = React.useMemo(() => {
    const map = new Map<string, string>();
    productQueries.forEach((query, index) => {
      if (query.data?.images?.[0]) {
        const imageUrl = getImageUrl(query.data.images[0]);
        if (imageUrl) {
          map.set(productIds[index], imageUrl);
        }
      }
    });
    return map;
  }, [productQueries, productIds]);

  // Track if campaign data has been loaded to prevent re-running effect
  const campaignLoadedRef = React.useRef(false);

  const form = useForm({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      name: '',
      type: 'sponsored_product' as AdType,
      budgetEur: 10,
      budgetUsd: 11,
      budgetKmf: 5000,
      startDate: '',
      endDate: '',
      dailyBudgetEur: undefined as number | undefined,
      dailyBudgetUsd: undefined as number | undefined,
      dailyBudgetKmf: undefined as number | undefined,
      bidAmountEur: undefined as number | undefined,
      bidAmountUsd: undefined as number | undefined,
      bidAmountKmf: undefined as number | undefined,
      targeting: undefined,
      items: undefined,
    },
  });

  // Populate form when editing - only run once when campaign data arrives
  React.useEffect(() => {
    if (campaign && isEditing && !campaignLoadedRef.current) {
      campaignLoadedRef.current = true;

      form.reset({
        name: campaign.name,
        type: campaign.type,
        budgetEur: campaign.budgetEur ?? 0,
        budgetUsd: campaign.budgetUsd ?? 0,
        budgetKmf: campaign.budgetKmf ?? 0,
        dailyBudgetEur: campaign.dailyBudgetEur ?? undefined,
        dailyBudgetUsd: campaign.dailyBudgetUsd ?? undefined,
        dailyBudgetKmf: campaign.dailyBudgetKmf ?? undefined,
        bidAmountEur: campaign.bidAmountEur ?? undefined,
        bidAmountUsd: campaign.bidAmountUsd ?? undefined,
        bidAmountKmf: campaign.bidAmountKmf ?? undefined,
        startDate: campaign.startDate ? new Date(campaign.startDate).toISOString() : '',
        endDate: campaign.endDate ? new Date(campaign.endDate).toISOString() : '',
        targeting: campaign.targeting ?? undefined,
      });

      // Populate simplified budget state (convert from EUR to default currency)
      const budgetInCurrency = fromEur(campaign.budgetEur ?? 0, defaultCurrency);
      setBudget(Math.round(budgetInCurrency * 100) / 100);

      if (campaign.dailyBudgetEur) {
        const dailyInCurrency = fromEur(campaign.dailyBudgetEur, defaultCurrency);
        setDailyBudget(Math.round(dailyInCurrency * 100) / 100);
      }

      if (campaign.bidAmountEur) {
        const bidInCurrency = fromEur(campaign.bidAmountEur, defaultCurrency);
        setBidAmount(Math.round(bidInCurrency * 100) / 100);
      }

      // Populate selected products from campaign items
      if (campaign.items && campaign.items.length > 0) {
        // For category_banner, include items with bannerUrl even without productRefId
        if (campaign.type === 'category_banner') {
          const firstItem = campaign.items[0];
          // Create a synthetic product for the banner
          if (firstItem?.bannerUrl || firstItem?.productRefId) {
            setSelectedProducts([{
              refId: firstItem.productRefId || 'banner',
              name: firstItem.productName || firstItem.title || campaign.name,
              image: firstItem.bannerUrl,
            }]);
          }
          setBannerText(firstItem?.message || firstItem?.title || '');
        } else {
          const products: SelectedProduct[] = campaign.items
            .filter((item) => item.productRefId)
            .map((item) => ({
              refId: item.productRefId!,
              name: item.productName || item.productRefId!,
              image: item.bannerUrl,
            }));
          setSelectedProducts(products);
        }
      }
    }
  }, [campaign, isEditing, defaultCurrency, form]);

  // Update selected products with images from fetched product data
  React.useEffect(() => {
    if (productImagesMap.size > 0 && selectedProducts.length > 0) {
      const hasProductWithoutImage = selectedProducts.some(
        (p) => !p.image && productImagesMap.has(p.refId)
      );
      if (hasProductWithoutImage) {
        setSelectedProducts((prev) =>
          prev.map((p) => ({
            ...p,
            image: p.image || productImagesMap.get(p.refId),
          }))
        );
      }
    }
  }, [productImagesMap, selectedProducts]);

  // Check if campaign can be edited (only draft or paused)
  const canEdit = !isEditing || !campaign || ['draft', 'paused'].includes(campaign.status);

  // Check if current campaign type requires products
  // When editing, use campaign.type directly since form.watch may not update immediately after reset
  const formType = form.watch('type');
  const currentType = (isEditing && campaign?.type) ? campaign.type : formType;
  const requiresProducts = PRODUCT_CAMPAIGN_TYPES.includes(currentType);

  // Get minimum budget for the current type (from server config or defaults)
  const currentTypeConfig = adTypeConfigs?.find((c) => c.type === currentType);
  const minBudgetEur = currentTypeConfig?.minBudgetEur ?? AD_TYPE_DEFAULTS[currentType]?.minBudgetEur ?? 5;
  const minBudgetInCurrency = fromEur(minBudgetEur, selectedCurrency);
  const pricePerUnitEur = currentTypeConfig?.priceEur ?? AD_TYPE_DEFAULTS[currentType]?.priceEur ?? 0.05;
  const pricePerUnitInCurrency = fromEur(pricePerUnitEur, selectedCurrency);

  // Handle selecting products (replaces current selection with new selection)
  const handleSelectProducts = (products: SelectedProduct[]) => {
    setSelectedProducts(products);
  };

  // Handle removing a product
  const handleRemoveProduct = (refId: string) => {
    setSelectedProducts(selectedProducts.filter((p) => p.refId !== refId));
  };

  const onSubmit = form.handleSubmit((data) => {
    // Convert budget from selected currency to all currencies
    const budgetEur = toEur(budget, selectedCurrency);
    const budgetUsd = fromEur(budgetEur, 'USD');
    const budgetKmf = fromEur(budgetEur, 'KMF');

    const dailyBudgetEur = dailyBudget ? toEur(dailyBudget, selectedCurrency) : undefined;
    const dailyBudgetUsd = dailyBudgetEur ? fromEur(dailyBudgetEur, 'USD') : undefined;
    const dailyBudgetKmf = dailyBudgetEur ? fromEur(dailyBudgetEur, 'KMF') : undefined;

    const bidAmountEur = bidAmount ? toEur(bidAmount, selectedCurrency) : undefined;
    const bidAmountUsd = bidAmountEur ? fromEur(bidAmountEur, 'USD') : undefined;
    const bidAmountKmf = bidAmountEur ? fromEur(bidAmountEur, 'KMF') : undefined;

    // Build items array from selected products
    let items;
    if (selectedProducts.length > 0) {
      // For category_banner, include banner URL and text
      if (data.type === 'category_banner') {
        const firstProductWithImage = selectedProducts.find(p => p.image);
        items = [{
          bannerUrl: firstProductWithImage?.image,
          productRefId: selectedProducts[0].refId,
          title: bannerText || data.name,
          message: bannerText || undefined,
        }];
      } else {
        items = selectedProducts.map((p) => ({ productRefId: p.refId }));
      }
    }

    // Build the complete data object with converted values
    const completeData = {
      ...data,
      budgetEur: Math.round(budgetEur * 100) / 100,
      budgetUsd: Math.round(budgetUsd * 100) / 100,
      budgetKmf: Math.round(budgetKmf),
      dailyBudgetEur: dailyBudgetEur ? Math.round(dailyBudgetEur * 100) / 100 : undefined,
      dailyBudgetUsd: dailyBudgetUsd ? Math.round(dailyBudgetUsd * 100) / 100 : undefined,
      dailyBudgetKmf: dailyBudgetKmf ? Math.round(dailyBudgetKmf) : undefined,
      bidAmountEur: bidAmountEur ? Math.round(bidAmountEur * 100) / 100 : undefined,
      bidAmountUsd: bidAmountUsd ? Math.round(bidAmountUsd * 100) / 100 : undefined,
      bidAmountKmf: bidAmountKmf ? Math.round(bidAmountKmf) : undefined,
      items,
    };

    if (isEditing && campaignId) {
      // Remove 'type' field - cannot be changed after creation
      const { type, ...updateData } = completeData;
      updateCampaign(
        { refId: campaignId, data: updateData },
        { onSuccess: () => navigate(`/ads/campaigns/${campaignId}`) }
      );
    } else {
      createCampaign(completeData as CampaignFormData, {
        onSuccess: (result) => {
          if (result?.refId) {
            navigate(`/ads/campaigns/${result.refId}`);
          } else {
            navigate('/ads/campaigns');
          }
        },
      });
    }
  });

  if (isEditing && isLoadingCampaign) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('ads.campaigns.form.basicInfo')}</CardTitle>
          <CardDescription>{t('ads.campaigns.form.basicInfoDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('ads.campaigns.form.name')}</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder={t('ads.campaigns.form.namePlaceholder')}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{t(form.formState.errors.name.message!)}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">{t('ads.campaigns.form.type')}</Label>
            <Select
              value={currentType}
              onValueChange={(value: AdType) => form.setValue('type', value)}
              disabled={isEditing}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('ads.campaigns.form.selectType')}>
                  {(() => {
                    const selectedType = CAMPAIGN_TYPES.find((t) => t.value === currentType);
                    if (selectedType) {
                      const Icon = selectedType.icon;
                      return (
                        <span className="flex items-center gap-2">
                          <Icon className="size-4" />
                          {t(selectedType.label)}
                        </span>
                      );
                    }
                    return t('ads.campaigns.form.selectType');
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-3">
                        <Icon className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <div className="font-medium">{t(type.label)}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {t(type.descriptionKey, type.descriptionFallback)}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('ads.campaigns.form.startDate')}</Label>
              <DateTimePicker
                value={form.watch('startDate')}
                onChange={(isoStr) => form.setValue('startDate', isoStr)}
                placeholder={t('ads.campaigns.form.selectDate')}
              />
              <p className="text-xs text-muted-foreground">Heure UTC</p>
              {form.formState.errors.startDate && (
                <p className="text-sm text-destructive">{t(form.formState.errors.startDate.message!)}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('ads.campaigns.form.endDate')}</Label>
              <DateTimePicker
                value={form.watch('endDate')}
                onChange={(isoStr) => form.setValue('endDate', isoStr)}
                placeholder={t('ads.campaigns.form.selectDate')}
              />
              <p className="text-xs text-muted-foreground">Heure UTC</p>
              {form.formState.errors.endDate && (
                <p className="text-sm text-destructive">{t(form.formState.errors.endDate.message!)}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget - Simplified */}
      <Card>
        <CardHeader>
          <CardTitle>{t('ads.campaigns.form.budget', 'Budget')}</CardTitle>
          <CardDescription>
            {t('ads.campaigns.form.budgetDescriptionSimple', 'Définissez combien vous souhaitez dépenser pour cette campagne')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Currency selector */}
          <div className="space-y-2">
            <Label>{t('ads.campaigns.form.currency', 'Devise')}</Label>
            <Select
              value={selectedCurrency}
              onValueChange={(value: Currency) => setSelectedCurrency(value)}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((curr) => (
                  <SelectItem key={curr} value={curr}>
                    {curr === 'EUR' && '🇪🇺 Euro (€)'}
                    {curr === 'USD' && '🇺🇸 Dollar ($)'}
                    {curr === 'KMF' && '🇰🇲 Franc comorien (KMF)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Minimum budget info */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1 text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  {t('ads.campaigns.form.minBudgetInfo', 'Budget minimum requis')}:{' '}
                  <span className="text-blue-700 dark:text-blue-300">
                    {selectedCurrency === 'KMF'
                      ? Math.round(minBudgetInCurrency).toLocaleString()
                      : minBudgetInCurrency.toFixed(2)}{' '}
                    {CURRENCY_SYMBOLS[selectedCurrency]}
                  </span>
                </p>
                <p className="text-blue-700 dark:text-blue-300">
                  {t('ads.campaigns.form.pricePerUnit', 'Prix unitaire')}:{' '}
                  {selectedCurrency === 'KMF'
                    ? Math.round(pricePerUnitInCurrency).toLocaleString()
                    : pricePerUnitInCurrency.toFixed(2)}{' '}
                  {CURRENCY_SYMBOLS[selectedCurrency]}
                  {currentTypeConfig?.priceUnit && (
                    <span className="text-blue-600 dark:text-blue-400 ml-1">
                      ({currentTypeConfig.priceUnit === 'per_click' && t('ads.priceUnit.perClick', 'par clic')}
                      {currentTypeConfig.priceUnit === 'per_day' && t('ads.priceUnit.perDay', 'par jour')}
                      {currentTypeConfig.priceUnit === 'per_week' && t('ads.priceUnit.perWeek', 'par semaine')}
                      {currentTypeConfig.priceUnit === 'per_1000' && t('ads.priceUnit.per1000', 'par 1000')}
                      {currentTypeConfig.priceUnit === 'per_unit' && t('ads.priceUnit.perUnit', 'par unité')}
                      {currentTypeConfig.priceUnit === 'per_month' && t('ads.priceUnit.perMonth', 'par mois')}
                      {currentTypeConfig.priceUnit === 'fixed' && t('ads.priceUnit.fixed', 'fixe')})
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Total Budget */}
          <div className="space-y-2">
            <Label htmlFor="budget">
              {t('ads.campaigns.form.totalBudgetSimple', 'Budget total')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('ads.campaigns.form.totalBudgetHelp', 'Le montant maximum que vous êtes prêt à dépenser pour cette campagne')}
            </p>
            <div className="relative">
              <Input
                id="budget"
                type="number"
                min={selectedCurrency === 'KMF' ? Math.round(minBudgetInCurrency) : minBudgetInCurrency}
                step={selectedCurrency === 'KMF' ? '100' : '0.01'}
                value={budget}
                onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                className={cn('pr-12', budget < minBudgetInCurrency && 'border-destructive')}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                {CURRENCY_SYMBOLS[selectedCurrency]}
              </span>
            </div>
            {budget < minBudgetInCurrency && (
              <p className="text-sm text-destructive">
                {t('ads.campaigns.form.budgetTooLow', 'Le budget doit être au minimum de {{amount}} {{currency}}', {
                  amount: selectedCurrency === 'KMF' ? Math.round(minBudgetInCurrency).toLocaleString() : minBudgetInCurrency.toFixed(2),
                  currency: CURRENCY_SYMBOLS[selectedCurrency],
                })}
              </p>
            )}
          </div>

          {/* Daily Budget (optional) */}
          <div className="space-y-2">
            <Label htmlFor="dailyBudget">
              {t('ads.campaigns.form.dailyBudgetSimple', 'Budget par jour')}
              <span className="text-muted-foreground text-sm ml-2">({t('common.optional', 'optionnel')})</span>
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('ads.campaigns.form.dailyBudgetHelp', 'Limite de dépense quotidienne pour éviter de tout dépenser trop vite')}
            </p>
            <div className="relative">
              <Input
                id="dailyBudget"
                type="number"
                min="0"
                step={selectedCurrency === 'KMF' ? '100' : '0.01'}
                value={dailyBudget ?? ''}
                onChange={(e) => setDailyBudget(e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder={t('ads.campaigns.form.dailyBudgetPlaceholder', 'Ex: 5')}
                className="pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                {CURRENCY_SYMBOLS[selectedCurrency]}
              </span>
            </div>
          </div>

          {/* Bid Amount (only for sponsored products and carousel) */}
          {(currentType === 'sponsored_product' || currentType === 'homepage_carousel') && (
            <div className="space-y-2">
              <Label htmlFor="bidAmount">
                {t('ads.campaigns.form.bidAmountSimple', 'Enchère par clic')}
                <span className="text-muted-foreground text-sm ml-2">({t('common.optional', 'optionnel')})</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('ads.campaigns.form.bidAmountHelp', 'Combien vous payez à chaque fois qu\'un client clique sur votre publicité')}
              </p>
              <div className="relative">
                <Input
                  id="bidAmount"
                  type="number"
                  min="0"
                  step={selectedCurrency === 'KMF' ? '10' : '0.01'}
                  value={bidAmount ?? ''}
                  onChange={(e) => setBidAmount(e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder={t('ads.campaigns.form.bidAmountPlaceholder', 'Ex: 0.10')}
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                  {CURRENCY_SYMBOLS[selectedCurrency]}
                </span>
              </div>
            </div>
          )}

          {/* Conversion preview */}
          {budget > 0 && (
            <div className="rounded-lg bg-muted/50 border p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Info className="size-4 text-muted-foreground" />
                {t('ads.campaigns.form.conversionPreview', 'Équivalent dans les autres devises')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                {CURRENCIES.filter((c) => c !== selectedCurrency).map((curr) => {
                  const budgetEur = toEur(budget, selectedCurrency);
                  const converted = fromEur(budgetEur, curr);
                  return (
                    <div key={curr} className="flex justify-between sm:flex-col sm:text-center p-2 rounded bg-background">
                      <span className="text-muted-foreground">{t('ads.campaigns.form.totalBudgetSimple', 'Budget total')}</span>
                      <span className="font-medium">
                        {formatPrice(converted, curr)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Selection - Only show for campaign types that need products */}
      {requiresProducts && (
        <Card>
          <CardHeader>
            <CardTitle>{t('ads.campaigns.form.products', 'Produits')}</CardTitle>
            <CardDescription>
              {t('ads.campaigns.form.productsDescription', 'Sélectionnez les produits à promouvoir')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected products list */}
            {selectedProducts.length > 0 && (
              <div className="space-y-2">
                {selectedProducts.map((product) => (
                  <div
                    key={product.refId}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="size-12 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="size-12 rounded bg-muted flex items-center justify-center shrink-0">
                        <Package className="size-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium line-clamp-1">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.refId}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => handleRemoveProduct(product.refId)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add product button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setIsProductPickerOpen(true)}
            >
              <Plus className="size-4 mr-2" />
              {selectedProducts.length > 0
                ? t('ads.campaigns.form.changeProducts', 'Modifier la sélection')
                : t('ads.campaigns.form.addProducts', 'Ajouter des produits')}
            </Button>

            {/* Hint for required products */}
            {selectedProducts.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('ads.campaigns.form.productRequired', 'Vous devez sélectionner au moins un produit pour ce type de campagne.')}
              </p>
            )}

            {/* Banner text input for category_banner */}
            {currentType === 'category_banner' && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="bannerText">
                    {t('ads.campaigns.form.bannerText', 'Texte promotionnel')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('ads.campaigns.form.bannerTextHelp', 'Ce texte sera affiché sur la bannière pour attirer l\'attention des clients')}
                  </p>
                  <Input
                    id="bannerText"
                    value={bannerText}
                    onChange={(e) => setBannerText(e.target.value)}
                    placeholder={t('ads.campaigns.form.bannerTextPlaceholder', 'Ex: -20% sur tous les articles !')}
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {bannerText.length}/60
                  </p>
                </div>

                {/* Banner preview */}
                {selectedProducts.find(p => p.image) && (
                  <div className="space-y-2">
                    <Label>{t('ads.campaigns.form.bannerPreview', 'Aperçu de la bannière')}</Label>
                    <div className="relative rounded-xl overflow-hidden border aspect-[16/9] max-w-md mx-auto">
                      <img
                        src={selectedProducts.find(p => p.image)?.image}
                        alt="Banner preview"
                        className="w-full h-full object-cover"
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      {/* Text overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white font-bold text-lg line-clamp-2 drop-shadow-lg">
                          {bannerText || form.watch('name') || t('ads.campaigns.form.bannerTextPlaceholder', 'Ex: -20% sur tous les articles !')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product Picker Dialog */}
      <ProductPickerDialog
        open={isProductPickerOpen}
        onOpenChange={setIsProductPickerOpen}
        multiSelect
        onSelectMultiple={handleSelectProducts}
        selectedProductIds={selectedProducts.map((p) => p.refId)}
        title={t('ads.campaigns.form.selectProducts', 'Sélectionner des produits')}
        description={t('ads.campaigns.form.selectProductsDescription', 'Choisissez les produits à promouvoir dans cette campagne')}
      />

      {/* Cannot Edit Warning */}
      {isEditing && !canEdit && (
        <Card className="border-warning bg-warning/10">
          <CardContent className="pt-4">
            <p className="text-sm font-medium text-warning">
              {t('ads.campaigns.cannotEditStatus', 'Cette campagne ne peut pas être modifiée car elle n\'est pas en statut brouillon ou en pause.')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Form Errors Summary */}
      {Object.keys(form.formState.errors).length > 0 && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <p className="text-sm font-medium text-destructive mb-2">
              {t('validation.formErrors', 'Veuillez corriger les erreurs suivantes :')}
            </p>
            <ul className="list-disc list-inside text-sm text-destructive">
              {Object.entries(form.formState.errors).map(([field, error]) => (
                <li key={field}>{field}: {error?.message ? t(error.message as string) : 'Invalid'}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isCreating || isUpdating || !canEdit}>
          {isCreating || isUpdating ? t('common.loading') : isEditing ? t('common.update', 'Modifier') : t('common.create')}
        </Button>
      </div>
    </form>
  );
}

interface DateTimePickerProps {
  value?: string; // ISO string
  onChange: (isoString: string) => void;
  placeholder?: string;
}

function DateTimePicker({ value, onChange, placeholder }: DateTimePickerProps) {
  const { t } = useTranslation();

  // Extract date and time from ISO string
  const extractTime = (isoStr: string): string => {
    if (!isoStr) return '00:00';
    const match = isoStr.match(/T(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
    try {
      const date = new Date(isoStr);
      if (isNaN(date.getTime())) return '00:00';
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    } catch {
      return '00:00';
    }
  };

  const [time, setTime] = React.useState(() => extractTime(value || ''));

  // Sync time when value changes
  React.useEffect(() => {
    if (value) {
      const newTime = extractTime(value);
      setTime(newTime);
    }
  }, [value]);

  const combineDateAndTime = (dateStr: string, timeStr: string): string => {
    const datePart = dateStr.split('T')[0];
    return `${datePart}T${timeStr}:00.000Z`;
  };

  const date = value ? new Date(value) : undefined;

  return (
    <div className="flex gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'flex-1 justify-start text-left font-normal',
              !date && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, 'dd/MM/yyyy') : placeholder || t('ads.campaigns.form.selectDate')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(newDate) => {
              if (newDate) {
                const dateStr = format(newDate, 'yyyy-MM-dd');
                onChange(combineDateAndTime(dateStr, time));
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <div className="relative w-28 sm:w-32">
        <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-white pointer-events-none" />
        <Input
          type="time"
          value={time}
          onChange={(e) => {
            const newTime = e.target.value;
            setTime(newTime);
            if (value) {
              onChange(combineDateAndTime(value, newTime));
            }
          }}
          className="pl-8 text-sm"
        />
      </div>
    </div>
  );
}
